import { useMemo, useState } from "react";
import { liveFilterTabs, liveFeedItems } from "@/mocks/liveData";
import { sideHotList } from "@/mocks/baoliaoData";

interface LiveFeedProps {
  search: string;
}

type SortKey = "new" | "hot";

export default function LiveFeed({ search }: LiveFeedProps) {
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState<SortKey>("new");
  const [limit, setLimit] = useState(8);

  const orderValue = (h: string) => (h === "高" ? 3 : h === "中" ? 2 : 1);

  const list = useMemo(() => {
    let items = liveFeedItems.filter((it) => cat === "all" || it.cat === cat);
    if (search.trim()) {
      const kw = search.trim();
      items = items.filter((it) => it.title.includes(kw) || it.cat.includes(kw) || it.tag.includes(kw));
    }
    const sorted = [...items];
    if (sort === "hot") sorted.sort((a, b) => orderValue(b.heat) - orderValue(a.heat));
    return sorted;
  }, [cat, search, sort]);

  const shown = list.slice(0, limit);

  return (
    <section id="live" className="py-10 md:py-16 bg-background-50">
      <div className="max-w-[1240px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* left feed */}
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-lg bg-primary-500 text-background-50 px-2.5 py-1 text-sm font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-live"></span>
                  LIVE
                </span>
                <div>
                  <h2 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight text-foreground-950">
                    实时爆料
                  </h2>
                  <p className="text-xs text-foreground-500 mt-0.5">跟随热点滚动更新，快速掌握正在发酵的瓜</p>
                </div>
              </div>
              {/* segmented sort control */}
              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100/70 p-1">
                <button
                  onClick={() => setSort("new")}
                  className={`px-3.5 py-1.5 text-xs rounded-full font-medium transition-colors whitespace-nowrap ${
                    sort === "new" ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  最新
                </button>
                <button
                  onClick={() => setSort("hot")}
                  className={`px-3.5 py-1.5 text-xs rounded-full font-medium transition-colors whitespace-nowrap ${
                    sort === "hot" ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  最热
                </button>
              </div>
            </div>

            {/* category chips */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {liveFilterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setCat(tab.key)}
                  className={`px-3.5 py-1.5 text-xs rounded-full font-medium transition-colors whitespace-nowrap ${
                    cat === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* feed list */}
            {shown.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-14 px-6 text-center">
                <i className="ri-inbox-2-line text-3xl text-foreground-300"></i>
                <p className="mt-3 text-sm text-foreground-600">没有匹配“{search}”的实时爆料，换个关键词试试吧</p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200/80 rounded-2xl border border-background-200 bg-background-50 overflow-hidden">
                {shown.map((item, idx) => (
                  <li
                    key={`${item.time}-${idx}`}
                    className="group flex items-start gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-background-100/70 transition-colors"
                  >
                    <span className="w-[52px] shrink-0 text-right font-heading text-xs font-semibold text-primary-600 pt-0.5 leading-none">
                      {item.time}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <span className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center">
                          {item.tag === "爆" ? (
                            <i className="ri-fire-fill text-primary-500 text-base"></i>
                          ) : item.tag === "热" ? (
                            <i className="ri-flashlight-fill text-accent-500 text-base"></i>
                          ) : (
                            <i className="ri-sparkling-2-fill text-secondary-400 text-base"></i>
                          )}
                        </span>
                        <a href="#track" className="text-sm md:text-[15px] leading-relaxed text-foreground-900 font-medium group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 pl-7 flex-wrap">
                        <span className="text-[11px] text-foreground-500">{item.cat}</span>
                        <span className="w-1 h-1 rounded-full bg-background-300"></span>
                        <span
                          className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold ${
                            item.tag === "爆"
                              ? "bg-primary-100 text-primary-700"
                              : item.tag === "热"
                              ? "bg-accent-100 text-accent-800"
                              : "bg-secondary-100 text-secondary-700"
                          }`}
                        >
                          {item.tag}
                        </span>
                        {item.heat === "高" && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-primary-600 font-medium">
                            <i className="ri-arrow-up-double-line text-xs"></i>热度飙升
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {list.length > limit && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => setLimit((n) => n + 8)}
                  className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-loader-4-line"></i>
                  加载更多实时爆料
                </button>
              </div>
            )}
          </div>

          {/* right hot board */}
          <aside>
            <div className="rounded-2xl border border-background-200 bg-background-100/60 p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-extrabold text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-fire-fill text-primary-500"></i>
                  实时热搜榜
                </h3>
                <span className="text-[11px] text-foreground-500">每5分钟刷新</span>
              </div>
              <ol className="flex flex-col gap-3">
                {sideHotList.map((item, i) => {
                  const rank = i + 1;
                  return (
                    <li key={item.rank}>
                      <a href="#track" className="group flex items-start gap-3 hover:bg-background-50 rounded-xl p-1.5 -m-1.5 transition-colors">
                        <span
                          className={`font-display text-base w-6 shrink-0 text-center ${
                            rank === 1
                              ? "text-primary-500"
                              : rank === 2
                              ? "text-accent-500"
                              : rank === 3
                              ? "text-secondary-600"
                              : "text-foreground-300"
                          }`}
                        >
                          {String(rank).padStart(2, "0")}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] leading-snug text-foreground-900 font-medium group-hover:text-primary-600 transition-colors truncate">
                            {item.title}
                          </span>
                          <span className="block text-[11px] text-foreground-500 mt-0.5">
                            {item.cat} · {item.heat} 热度
                          </span>
                        </span>
                        {rank <= 3 && (
                          <span className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center">
                            {rank === 1 ? (
                              <i className="ri-fire-fill text-primary-500 text-base"></i>
                            ) : (
                              <i className="ri-arrow-up-line text-accent-500 text-xs"></i>
                            )}
                          </span>
                        )}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="mt-5 rounded-2xl border border-background-200 bg-background-50 p-5">
              <p className="text-xs text-foreground-500 leading-relaxed">
                <i className="ri-information-line text-primary-500 mr-1"></i>
                提示：爆料网所有实时爆料均基于公开可见信息与多方信源整理，处于核实状态的内容会同步标注，并在热点追踪中持续补充最新进展。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}